import type * as THREE from 'three';

/** What a background compile needs of a WebGLRenderer: its synchronous compile and the state it keeps per material. */
export interface ShaderCompiler {
  compile(scene: THREE.Object3D, camera: THREE.Camera, targetScene?: THREE.Scene | null): Set<THREE.Material>;
  properties: { get(object: unknown): unknown };
}

/**
 * `renderer.compileAsync` without its failure mode. three compiles the programs, then polls each material's
 * `currentProgram.isReady()` on a timer of its own; once the material or the renderer is disposed mid-compile — the
 * editor left, or the floors below rebuilt, while their shaders were still compiling, which a slow GPU makes likely —
 * the program is gone and the poll throws from that timer, uncaught. The same compile is polled here: it stops when
 * `alive()` turns false, and a material whose program has gone counts as done.
 */
export function compileInBackground(renderer: ShaderCompiler, scene: THREE.Object3D, camera: THREE.Camera, target: THREE.Scene | null, alive: () => boolean): Promise<void> {
  const materials = renderer.compile(scene, camera, target);
  return new Promise(resolve => {
    const check = () => {
      if (!alive()) { resolve(); return; }
      for (const material of materials) {
        const program = (renderer.properties.get(material) as { currentProgram?: { isReady(): boolean } }).currentProgram;
        if (!program || program.isReady()) materials.delete(material);
      }
      if (materials.size) setTimeout(check, 10); else resolve();
    };
    check();
  });
}
